import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, ViewEncapsulation, Input, Output, ViewChild, NgModule } from '@angular/core';
import { of, forkJoin, BehaviorSubject } from 'rxjs';
import { map, retryWhen, delay, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

class SearchComponentElementIds {
    constructor(elementIdOptions) {
        this.searchInputId = elementIdOptions.searchInputId || "ggcSearchInput";
        this.searchButtonId = elementIdOptions.searchButtonId || "ggcSearchButton";
        this.suggestionListId =
            elementIdOptions.suggestionListId || "ggcSuggestionList";
        this.searchLabelId = elementIdOptions.searchLabelId || "ggcSearchLabel";
    }
}

var SearchComponentEventTypes;
(function (SearchComponentEventTypes) {
    SearchComponentEventTypes["RESULT_INVALIDATED"] = "resultInvalidated";
    SearchComponentEventTypes["SEARCH_RESULT"] = "searchResult";
    SearchComponentEventTypes["SEARCH_RESULT_ERROR"] = "searchResultError";
    SearchComponentEventTypes["SEARCH_SUGGESTION_ERROR"] = "searchSuggestionError";
})(SearchComponentEventTypes || (SearchComponentEventTypes = {}));
class SearchComponentEvent {
    /**
     * @param type searchResult, resultInvalidated
     * @param message de gebruiker heeft op een button geklikt, er is een nieuw searchResult, vorige searchResult is niet meer geldig, ...
     * @param value optioneel
     */
    constructor(type, message, value) {
        this.type = type;
        this.message = message;
        this.value = value;
    }
}

class SearchLocationService {
    constructor(httpClient) {
        this.httpClient = httpClient;
        this.baseUrl = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup";
        this.queryUrl = "?fl=*&id=";
    }
    search(term) {
        return this.httpClient
            .get(this.baseUrl + this.queryUrl + term)
            .pipe(map((result) => result.response), retryWhen((errors) => {
            let retries = 0;
            return errors.pipe(delay(500), map((error) => {
                if (retries++ === 3) {
                    throw error;
                }
            }));
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchLocationService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchLocationService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchLocationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

class AdditionalSuggestionSourceService {
    search(_) {
        return of([]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AdditionalSuggestionSourceService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AdditionalSuggestionSourceService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AdditionalSuggestionSourceService, decorators: [{
            type: Injectable
        }] });

class SearchSuggestionService {
    constructor(httpClient, additionalSuggestionSourceService) {
        this.httpClient = httpClient;
        this.additionalSuggestionSourceService = additionalSuggestionSourceService;
        this.minQueryLength = 1;
        this.numberOfSuggestions = 5;
        this.baseUrl = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest?";
        this.debounceTime = 400;
        this.queryUrl = "q=";
        this.queryRows = "rows=";
        this.filterQueryKey = "fq=";
    }
    searchOnTermChange(terms) {
        return terms.pipe(debounceTime(this.debounceTime), distinctUntilChanged(), switchMap((term) => term.length >= this.minQueryLength ? this.search(term) : of(null)));
    }
    search(term) {
        const url = this.constructUrl(term);
        return forkJoin([
            this.additionalSuggestionSourceService.search(term),
            this.httpClient.get(url).pipe(map((res) => res.response), retryWhen((errors) => {
                let retries = 0;
                return errors.pipe(delay(500), map((error) => {
                    if (retries++ === 3) {
                        throw error;
                    }
                }));
            })),
        ]).pipe(map(([altSuggestions, pdokResponse]) => {
            altSuggestions.forEach((suggestion) => (suggestion.source = suggestion.source || "alt"));
            pdokResponse.numFound += altSuggestions.length;
            pdokResponse.docs.push(...altSuggestions);
            pdokResponse.docs.splice(this.numberOfSuggestions);
            return pdokResponse;
        }));
    }
    constructUrl(term) {
        const parameterArray = [];
        parameterArray.push(this.queryUrl + encodeURIComponent(term), this.queryRows + this.numberOfSuggestions);
        if (this.filterQuery) {
            parameterArray.push(this.filterQueryKey + this.filterQuery);
        }
        return this.baseUrl + parameterArray.join("&");
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchSuggestionService, deps: [{ token: i1.HttpClient }, { token: AdditionalSuggestionSourceService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchSuggestionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchSuggestionService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: AdditionalSuggestionSourceService }]; } });

const BTN_SUFFIX = "btn-form-icon";
class SearchComponent {
    get classClearButton() {
        return this._classClearButton;
    }
    set classClearButton(value) {
        this._classClearButton = value;
        this.clsClearButton = value + " " + BTN_SUFFIX;
    }
    get searchTerm() {
        return this._searchTerm;
    }
    set searchTerm(value) {
        this._searchTerm = this.inputValue = value.trim();
    }
    get classSearchButton() {
        return this._classSearchButton;
    }
    set classSearchButton(value) {
        this._classSearchButton = value;
        this.clsSearchButton = value + " " + BTN_SUFFIX;
    }
    constructor(searchSuggestionService, searchLocationService) {
        this.searchSuggestionService = searchSuggestionService;
        this.searchLocationService = searchLocationService;
        this.minQueryLength = 1;
        this.triggerSearch = true;
        this.elementIds = new SearchComponentElementIds({});
        this.placeholderTextAndAriaLabel = "Voer locatie in";
        this.events = new EventEmitter();
        this.activeIndex = -1;
        this.inputValue = "";
        this.suggestions = [];
        this.showSuggestions = false;
        this.noSuggestionsFound = false;
        this._classSearchButton = "fal fa-search";
        this._classClearButton = "fal fa-times";
        this.hasInitialSearchterm = false;
        this.searchTerm$ = new BehaviorSubject("");
        this.clsSearchButton = this._classSearchButton + " " + BTN_SUFFIX;
        this.clsClearButton = this._classClearButton + " " + BTN_SUFFIX;
    }
    ngOnInit() {
        // Set optional configuration for searchSuggestionService
        if (this.numberOfSuggestions) {
            this.searchSuggestionService.numberOfSuggestions =
                this.numberOfSuggestions;
        }
        if (this.filterQuery) {
            this.searchSuggestionService.filterQuery = this.filterQuery;
        }
        if (this.minQueryLength > 0) {
            this.searchSuggestionService.minQueryLength = this.minQueryLength;
        }
        // Subscribe to searchSuggestionService.search
        this.searchSuggestionService.searchOnTermChange(this.searchTerm$).subscribe((results) => {
            this.processSuggestionsResult(results);
        }, (error) => {
            this.processError(error, SearchComponentEventTypes.SEARCH_SUGGESTION_ERROR);
        });
        // Search for suggestions when initialSearchterm is present
        if (this.initialSearchterm) {
            const trimmed = this.initialSearchterm.trim();
            if (trimmed) {
                this.hasInitialSearchterm = true;
                this.inputValue = trimmed;
                this.searchTerm$.next(trimmed);
            }
        }
        if (this.searchTerm && this.triggerSearch) {
            this.hasInitialSearchterm = true;
            this.searchTerm$.next(this._searchTerm);
        }
    }
    clearSearchTerm() {
        this.inputValue = "";
        this.searchTerm$.next(this.inputValue);
        this.resetSuggestionsAndResult();
    }
    lookupPdok(suggestion) {
        this.suggestions.length = 0;
        this.inputValue = suggestion.weergavenaam;
        this.weergavenaamCache = suggestion.weergavenaam;
        if (typeof suggestion.source === "undefined") {
            this.searchLocationService.search(suggestion.id).subscribe((result) => this.processResultLocation(result), (error) => {
                this.processError(error, SearchComponentEventTypes.SEARCH_RESULT_ERROR);
            });
        }
        else {
            this.processResultLocation({
                docs: [suggestion],
                numFound: 1,
                maxScore: 1,
                start: 1,
            });
        }
    }
    onInputUp($event) {
        switch ($event.key) {
            case "Esc":
            case "Escape":
                this.input.nativeElement.value = "";
                this.clearSearchTerm();
                break;
            case "ArrowDown":
                if (this.suggestions.length > 0) {
                    $event.preventDefault();
                    this.activeIndex = 0;
                    this.listBox.nativeElement.focus();
                }
                break;
            default:
                this.searchForSuggestions($event.target.value);
        }
    }
    onListKeydown($event) {
        switch ($event.key) {
            case "ArrowDown":
            case "ArrowUp":
            case "Home":
            case "End":
                // Prevent scrolling
                $event.preventDefault();
                break;
        }
    }
    onListKeyup($event) {
        if ($event.isComposing || this.isSpecialKeyPressed($event)) {
            return;
        }
        let preventDefault = true;
        switch ($event.key) {
            case "ArrowDown":
                this.activeIndex++;
                if (this.activeIndex === this.suggestions.length) {
                    this.activeIndex = 0;
                }
                break;
            case "ArrowUp":
                this.activeIndex--;
                if (this.activeIndex < 0) {
                    this.input.nativeElement.focus();
                    this.activeIndex = -1;
                }
                break;
            case "End":
                this.activeIndex = this.suggestions.length - 1;
                break;
            case "Home":
                this.activeIndex = 0;
                break;
            case "Enter":
                this.lookupPdok(this.suggestions[this.activeIndex]);
                break;
            case "Esc":
            case "Escape":
                this.resetSuggestionsAndResult();
                this.input.nativeElement.focus();
                break;
            default:
                if ($event.key.length === 1) {
                    // Single character
                    this.input.nativeElement.value += $event.key;
                    this.input.nativeElement.focus();
                    this.searchForSuggestions(this.input.nativeElement.value);
                }
                preventDefault = false;
        }
        if (preventDefault) {
            $event.preventDefault();
            this.scrollIntoViewIfNeeded(this.input.nativeElement);
        }
    }
    processSuggestionsResult(response) {
        this.resetSuggestionsAndResult();
        if (response && response.numFound > -1) {
            this.showSuggestions = true;
            this.suggestions = response.docs;
            if (response.numFound > 0) {
                this.noSuggestionsFound = false;
                this.checkAndSearchInitialSearchterm();
            }
            else {
                this.noSuggestionsFound = true;
            }
            this.clearHasInitialSearchterm();
        }
    }
    resetSuggestionsAndResult() {
        if (this.result) {
            this.events.emit(new SearchComponentEvent(SearchComponentEventTypes.RESULT_INVALIDATED, "Het vorige zoekresultaat is niet meer geldig"));
        }
        this.activeIndex = -1;
        this.suggestions.length = 0;
        this.noSuggestionsFound = false;
        this.result = undefined;
        this.weergavenaamCache = undefined;
        this.showSuggestions = false;
    }
    searchForSuggestions(value) {
        if (value !== this.weergavenaamCache) {
            this.searchTerm$.next(value);
        }
    }
    searchOnEnter() {
        if (this.result) {
            this.events.emit(new SearchComponentEvent(SearchComponentEventTypes.SEARCH_RESULT, "Er is een nieuw zoekresultaat", this.result));
        }
        else if (this.suggestions.length > 0) {
            this.lookupFirstSuggestion();
        }
        else if (!this.weergavenaamCache) {
            this.searchForSuggestions(this.inputValue);
        }
    }
    checkAndSearchInitialSearchterm() {
        if (this.hasInitialSearchterm && this.suggestions.length === 1) {
            this.lookupFirstSuggestion();
        }
    }
    clearHasInitialSearchterm() {
        if (this.hasInitialSearchterm) {
            this.hasInitialSearchterm = false;
        }
    }
    isSpecialKeyPressed($event) {
        return $event.altKey || $event.ctrlKey || $event.metaKey;
    }
    lookupFirstSuggestion() {
        this.lookupPdok(this.suggestions[0]);
    }
    processResultLocation(result) {
        this.result = result;
        this.events.emit(new SearchComponentEvent(SearchComponentEventTypes.SEARCH_RESULT, "Er is een nieuw zoekresultaat", this.result));
        this.showSuggestions = false;
    }
    processError(error, type) {
        let errorMessage;
        if (error.error instanceof ErrorEvent) {
            errorMessage = error.message ? error.message : error.toString();
        }
        else {
            errorMessage = `${error.status} - ${error.statusText || ""} - ${error.message}`;
        }
        this.events.emit(new SearchComponentEvent(type, "Er is een fout opgetreden bij het benaderen van de locatieserver: " +
            errorMessage +
            ". Herlaad de pagina om het opnieuw te proberen.", error));
    }
    scrollIntoViewIfNeeded(target) {
        if (target.getBoundingClientRect().bottom > window.innerHeight) {
            target.scrollIntoView(false);
        }
        if (target.getBoundingClientRect().top < 0) {
            target.scrollIntoView();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchComponent, deps: [{ token: SearchSuggestionService }, { token: SearchLocationService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.3", type: SearchComponent, selector: "ggc-search", inputs: { numberOfSuggestions: "numberOfSuggestions", minQueryLength: "minQueryLength", filterQuery: "filterQuery", initialSearchterm: "initialSearchterm", triggerSearch: "triggerSearch", labelText: "labelText", elementIds: "elementIds", placeholderTextAndAriaLabel: "placeholderTextAndAriaLabel", classClearButton: "classClearButton", searchTerm: "searchTerm", classSearchButton: "classSearchButton" }, outputs: { events: "events" }, providers: [SearchSuggestionService, SearchLocationService], viewQueries: [{ propertyName: "listBox", first: true, predicate: ["listbox"], descendants: true, static: true }, { propertyName: "input", first: true, predicate: ["input"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"form-group form-group-ggc-search-suggestions\">\n  <label\n    *ngIf=\"labelText\"\n    [attr.id]=\"elementIds.searchLabelId\"\n    [attr.for]=\"elementIds.searchInputId\"\n    >{{ labelText }}</label\n  >\n  <div class=\"input-group\" aria-haspopup=\"listbox\">\n    <input\n      #input\n      [attr.id]=\"elementIds.searchInputId\"\n      [attr.aria-controls]=\"elementIds.suggestionListId\"\n      [attr.aria-label]=\"!labelText ? placeholderTextAndAriaLabel : null\"\n      (keyup)=\"onInputUp($event)\"\n      (keyup.enter)=\"searchOnEnter()\"\n      [value]=\"inputValue\"\n      class=\"form-control\"\n      role=\"combobox\"\n      aria-autocomplete=\"list\"\n      placeholder=\"{{ placeholderTextAndAriaLabel }}\"\n      autocomplete=\"off\"\n    />\n    <div class=\"input-group-append\">\n      <button\n        *ngIf=\"inputValue?.length > 0\"\n        aria-label=\"Zoekterm wissen\"\n        id=\"clear\"\n        (click)=\"clearSearchTerm()\"\n        class=\"btn btn-form\"\n      >\n        <span [ngClass]=\"clsClearButton\" aria-hidden=\"true\"></span>\n      </button>\n      <button\n        [attr.id]=\"elementIds.searchButtonId\"\n        aria-label=\"Zoeken\"\n        class=\"btn btn-form\"\n        (click)=\"searchOnEnter()\"\n      >\n        <span [ngClass]=\"clsSearchButton\" aria-hidden=\"true\"></span>\n      </button>\n    </div>\n  </div>\n  <div\n    #listbox\n    role=\"listbox\"\n    class=\"list-group list-group-ggc-search-suggestions\"\n    tabindex=\"0\"\n    [attr.aria-activedescendant]=\"\n      activeIndex >= 0 ? elementIds.suggestionListId + '-' + activeIndex : ''\n    \"\n    (keydown)=\"onListKeydown($event)\"\n    (keyup)=\"onListKeyup($event)\"\n    [attr.id]=\"elementIds.suggestionListId\"\n    [hidden]=\"!showSuggestions\"\n    [attr.aria-labelledby]=\"elementIds.searchLabelId\"\n  >\n    <button\n      class=\"list-group-item btn btn-link px-2\"\n      [ngClass]=\"{ 'btn-active': activeIndex === idx }\"\n      *ngFor=\"let suggestion of suggestions; let idx = index\"\n      type=\"button\"\n      role=\"option\"\n      [attr.id]=\"elementIds.suggestionListId + '-' + idx\"\n      (click)=\"lookupPdok(suggestion)\"\n    >\n      {{ suggestion.weergavenaam }}\n    </button>\n\n    <span class=\"list-group-item\" *ngIf=\"noSuggestionsFound\"\n      >Geen resultaat gevonden</span\n    >\n  </div>\n</div>\n", styles: [".list-group:focus-within .btn-active{outline:2px solid black}.list-group .btn{align-items:center;display:inline-flex;text-align:center}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchComponent, decorators: [{
            type: Component,
            args: [{ selector: "ggc-search", providers: [SearchSuggestionService, SearchLocationService], encapsulation: ViewEncapsulation.None, template: "<div class=\"form-group form-group-ggc-search-suggestions\">\n  <label\n    *ngIf=\"labelText\"\n    [attr.id]=\"elementIds.searchLabelId\"\n    [attr.for]=\"elementIds.searchInputId\"\n    >{{ labelText }}</label\n  >\n  <div class=\"input-group\" aria-haspopup=\"listbox\">\n    <input\n      #input\n      [attr.id]=\"elementIds.searchInputId\"\n      [attr.aria-controls]=\"elementIds.suggestionListId\"\n      [attr.aria-label]=\"!labelText ? placeholderTextAndAriaLabel : null\"\n      (keyup)=\"onInputUp($event)\"\n      (keyup.enter)=\"searchOnEnter()\"\n      [value]=\"inputValue\"\n      class=\"form-control\"\n      role=\"combobox\"\n      aria-autocomplete=\"list\"\n      placeholder=\"{{ placeholderTextAndAriaLabel }}\"\n      autocomplete=\"off\"\n    />\n    <div class=\"input-group-append\">\n      <button\n        *ngIf=\"inputValue?.length > 0\"\n        aria-label=\"Zoekterm wissen\"\n        id=\"clear\"\n        (click)=\"clearSearchTerm()\"\n        class=\"btn btn-form\"\n      >\n        <span [ngClass]=\"clsClearButton\" aria-hidden=\"true\"></span>\n      </button>\n      <button\n        [attr.id]=\"elementIds.searchButtonId\"\n        aria-label=\"Zoeken\"\n        class=\"btn btn-form\"\n        (click)=\"searchOnEnter()\"\n      >\n        <span [ngClass]=\"clsSearchButton\" aria-hidden=\"true\"></span>\n      </button>\n    </div>\n  </div>\n  <div\n    #listbox\n    role=\"listbox\"\n    class=\"list-group list-group-ggc-search-suggestions\"\n    tabindex=\"0\"\n    [attr.aria-activedescendant]=\"\n      activeIndex >= 0 ? elementIds.suggestionListId + '-' + activeIndex : ''\n    \"\n    (keydown)=\"onListKeydown($event)\"\n    (keyup)=\"onListKeyup($event)\"\n    [attr.id]=\"elementIds.suggestionListId\"\n    [hidden]=\"!showSuggestions\"\n    [attr.aria-labelledby]=\"elementIds.searchLabelId\"\n  >\n    <button\n      class=\"list-group-item btn btn-link px-2\"\n      [ngClass]=\"{ 'btn-active': activeIndex === idx }\"\n      *ngFor=\"let suggestion of suggestions; let idx = index\"\n      type=\"button\"\n      role=\"option\"\n      [attr.id]=\"elementIds.suggestionListId + '-' + idx\"\n      (click)=\"lookupPdok(suggestion)\"\n    >\n      {{ suggestion.weergavenaam }}\n    </button>\n\n    <span class=\"list-group-item\" *ngIf=\"noSuggestionsFound\"\n      >Geen resultaat gevonden</span\n    >\n  </div>\n</div>\n", styles: [".list-group:focus-within .btn-active{outline:2px solid black}.list-group .btn{align-items:center;display:inline-flex;text-align:center}\n"] }]
        }], ctorParameters: function () { return [{ type: SearchSuggestionService }, { type: SearchLocationService }]; }, propDecorators: { numberOfSuggestions: [{
                type: Input
            }], minQueryLength: [{
                type: Input
            }], filterQuery: [{
                type: Input
            }], initialSearchterm: [{
                type: Input
            }], triggerSearch: [{
                type: Input
            }], labelText: [{
                type: Input
            }], elementIds: [{
                type: Input
            }], placeholderTextAndAriaLabel: [{
                type: Input
            }], events: [{
                type: Output
            }], listBox: [{
                type: ViewChild,
                args: ["listbox", { static: true }]
            }], input: [{
                type: ViewChild,
                args: ["input", { static: true }]
            }], classClearButton: [{
                type: Input
            }], searchTerm: [{
                type: Input
            }], classSearchButton: [{
                type: Input
            }] } });

class GgcSearchModule {
    static forRoot(config = {}) {
        return {
            ngModule: GgcSearchModule,
            providers: [
                config?.suggestSources || {
                    provide: AdditionalSuggestionSourceService,
                    useClass: AdditionalSuggestionSourceService,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: GgcSearchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.3", ngImport: i0, type: GgcSearchModule, declarations: [SearchComponent], imports: [CommonModule, HttpClientModule], exports: [SearchComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: GgcSearchModule, providers: [
            {
                provide: AdditionalSuggestionSourceService,
                useClass: AdditionalSuggestionSourceService,
            },
        ], imports: [CommonModule, HttpClientModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: GgcSearchModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SearchComponent],
                    imports: [CommonModule, HttpClientModule],
                    exports: [SearchComponent],
                    providers: [
                        {
                            provide: AdditionalSuggestionSourceService,
                            useClass: AdditionalSuggestionSourceService,
                        },
                    ],
                }]
        }] });

class SearchRdService {
    search(query) {
        const patt = new RegExp(/( +.*?\s?)?(\d{1,18}((\.|,)\d+)?)(;|,|\s)\s*(\d{1,18}((\.|,)\d+)?)/);
        if (patt.test(query)) {
            const match = query.match(patt);
            if (match) {
                const value1 = match[2];
                const value2 = match[6];
                const newValue = value1.replace(",", ".") + ", " + value2.replace(",", ".");
                const weergave = match[2] + " " + match[6];
                return of([
                    {
                        score: 1,
                        source: "coordinate",
                        type: "",
                        id: newValue,
                        weergavenaam: "RD-coördinaten: " + weergave,
                    },
                ]);
            }
        }
        return of([]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchRdService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchRdService, providedIn: "root" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: SearchRdService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }] });

/*
 * Public API Surface of ggc-search
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AdditionalSuggestionSourceService, GgcSearchModule, SearchComponent, SearchComponentElementIds, SearchComponentEvent, SearchComponentEventTypes, SearchLocationService, SearchRdService, SearchSuggestionService };
